import {
    AccountBalancesWidget,
    ChartWidget,
    CHAT_MESSAGE_PADDING_HORIZONTAL,
    CreditCardRateCalloutWidget,
    ExistingGoalsBadgesWidget,
    GoalSummaryCardWidget,
    InfoCardsWidget,
    InlineDataTableWidget,
    MoneyMapWidget,
    SproutWidgetFrame,
} from '@fruitful/agent-ui';
import {
    buildSpendGoalsConfirmedMessage,
    type SendSilentMessageOptions,
} from '@fruitful/llm-service-shared';
import {
    type AccountBalancesWidgetProps,
    type AhaMomentWidgetProps,
    AnalyticsUserEvent,
    type CardDetailsWidgetProps,
    type ChartWidgetProps,
    type EmailSupportWidgetProps,
    type EstimatedBillsWidgetProps,
    type ExistingGoalsBadgesWidgetProps,
    type FreeToSpendWidgetProps,
    type GoalSummaryCardWidgetProps,
    type IncomeSplitWidgetProps,
    type InfoCardsWidgetProps,
    type InlineDataTableWidgetProps,
    type MoneyMapWidgetProps,
    type SendToGuideWidgetProps,
    type SpendGoalsWidgetProps,
    type TransactionsWidgetProps,
    WidgetType,
} from '@fruitful/types';
import { useFeatureGate } from '@statsig/expo-bindings';
import { router } from 'expo-router';
import React from 'react';
import { YStack } from 'smoothie';

import { FeatureFlags } from '../../../feature-flags/FeatureFlags';
import { track } from '../../../logger/Logger';
import { AhaMomentWidget, SpendGoalsWidget } from '../ChatWidgets';
import { CardDetailsWidget } from '../widgets/CardDetailsWidget';
import { EmailSupportWidget } from '../widgets/EmailSupportWidget';
import { FreeToSpendWidget } from '../widgets/FreeToSpendWidget';
import { IncomeSplitWidget } from '../widgets/IncomeSplitWidget';
import { SendToGuideWidget } from '../widgets/SendToGuideWidget';
import { TransactionsWidget } from '../widgets/TransactionsWidget';

export interface ChatWidgetHandlers {
    onSendSilentMessage: (
        message: string,
        options?: SendSilentMessageOptions,
    ) => Promise<void>;
    onSendMessageWithAgentPayload: (
        displayText: string,
        agentPayload: string,
    ) => Promise<void>;
}

interface ChatWidgetRendererProps {
    messageId: string;
    widget: WidgetType;
    props: Record<string, unknown> | null;
    isStreaming: boolean;
    handlers: ChatWidgetHandlers;
    onEntranceComplete?: () => void;
}

export const ChatWidgetRenderer = ({
    messageId,
    widget,
    props: widgetProps,
    isStreaming,
    handlers,
    onEntranceComplete,
}: ChatWidgetRendererProps) => {
    const { onSendMessageWithAgentPayload } = handlers;

    // CMM widgets keep their own layout (maxWidth 345) — do not use SproutWidgetFrame.
    if (widget === WidgetType.AhaMoment) {
        const ahaMomentProps = widgetProps as unknown as AhaMomentWidgetProps;

        if (ahaMomentProps.autoNavigate === true) {
            return <AhaMomentWidget {...ahaMomentProps} />;
        }

        return (
            <YStack
                width="100%"
                paddingHorizontal={CHAT_MESSAGE_PADDING_HORIZONTAL}
                alignItems="center"
            >
                <AhaMomentWidget {...ahaMomentProps} />
            </YStack>
        );
    }

    if (widget === WidgetType.SpendGoals) {
        return (
            <YStack
                width="100%"
                paddingHorizontal={CHAT_MESSAGE_PADDING_HORIZONTAL}
                alignItems="center"
            >
                <SpendGoalsWidget
                    {...(widgetProps as unknown as SpendGoalsWidgetProps)}
                    isConfirmDisabled={isStreaming}
                    onConfirm={({
                        monthlySpendCents,
                        monthlyGoalsSpendCents,
                    }) => {
                        void onSendMessageWithAgentPayload(
                            'Looks good',
                            buildSpendGoalsConfirmedMessage({
                                monthlySpendCents,
                                monthlyGoalsSpendCents,
                            }),
                        );
                    }}
                />
            </YStack>
        );
    }

    if (widget === WidgetType.MultiSelectInput) {
        return null;
    }

    // Rendered in the composer footer (see OnboardingChatContainer), not inline.
    if (widget === WidgetType.SliderInput) {
        return null;
    }

    // Rendered in the composer footer (see OnboardingChatContainer), not inline.
    if (widget === WidgetType.DebtBalanceSliders) {
        return null;
    }

    // Rendered in the composer footer (see OnboardingChatContainer), not inline.
    if (widget === WidgetType.IncomeCollection) {
        return null;
    }

    if (widget === WidgetType.CreditCardRateCallout) {
        return (
            <SproutWidgetFrame>
                <CreditCardRateCalloutWidget />
            </SproutWidgetFrame>
        );
    }

    if (widget === WidgetType.GoalSummaryCard) {
        return (
            <YStack
                width="100%"
                paddingHorizontal={CHAT_MESSAGE_PADDING_HORIZONTAL}
                alignItems="center"
            >
                <GoalSummaryCardWidget
                    {...(widgetProps as unknown as GoalSummaryCardWidgetProps)}
                />
            </YStack>
        );
    }

    // Sprout widgets: centered, max 400px; chart, money map, income split, and
    // handoff cards get frosted chrome.
    if (widget === WidgetType.InlineDataTable) {
        return (
            <SproutWidgetFrame>
                <InlineDataTableWidget
                    {...(widgetProps as unknown as InlineDataTableWidgetProps)}
                />
            </SproutWidgetFrame>
        );
    }

    if (widget === WidgetType.Chart) {
        return (
            <SproutWidgetFrame frosted>
                <ChartWidget
                    {...(widgetProps as unknown as ChartWidgetProps)}
                    onSelect={({
                        isLegendSelect,
                        chartKind,
                        chartSeriesCount,
                    }) => {
                        track(
                            isLegendSelect
                                ? AnalyticsUserEvent.AI_CHAT_WIDGET_CHART_LEGEND_SELECT
                                : AnalyticsUserEvent.AI_CHAT_WIDGET_CHART_SELECT,
                            { chartKind, chartSeriesCount },
                        );
                    }}
                />
            </SproutWidgetFrame>
        );
    }

    if (widget === WidgetType.FreeToSpend) {
        return (
            <SproutWidgetFrame>
                <FreeToSpendWidget
                    {...(widgetProps as unknown as FreeToSpendWidgetProps)}
                />
            </SproutWidgetFrame>
        );
    }

    if (widget === WidgetType.CardDetails) {
        return (
            <SproutWidgetFrame>
                <CardDetailsWidget
                    {...(widgetProps as unknown as CardDetailsWidgetProps)}
                />
            </SproutWidgetFrame>
        );
    }

    if (widget === WidgetType.AccountBalances) {
        return (
            <SproutWidgetFrame>
                <AccountBalancesWidget
                    {...(widgetProps as unknown as AccountBalancesWidgetProps)}
                    onPressAccount={(account) => {
                        track(
                            AnalyticsUserEvent.AI_CHAT_WIDGET_ACCOUNT_BALANCES_OPEN,
                            { accountName: account.name },
                        );
                        router.push(
                            `/banking/space-details?syncteraAccountId=${account.syncteraAccountId}`,
                        );
                    }}
                />
            </SproutWidgetFrame>
        );
    }

    if (widget === WidgetType.Transactions) {
        return (
            <SproutWidgetFrame>
                <TransactionsWidget
                    {...(widgetProps as unknown as TransactionsWidgetProps)}
                />
            </SproutWidgetFrame>
        );
    }

    if (widget === WidgetType.IncomeSplit) {
        return (
            <SproutWidgetFrame frosted contentPadding={0}>
                <IncomeSplitWidget
                    {...(widgetProps as unknown as IncomeSplitWidgetProps)}
                />
            </SproutWidgetFrame>
        );
    }

    if (widget === WidgetType.MoneyMap) {
        return (
            <SproutWidgetFrame frosted>
                <MoneyMapWidget
                    {...(widgetProps as unknown as MoneyMapWidgetProps)}
                    onFilterChange={(filter) => {
                        track(
                            AnalyticsUserEvent.AI_CHAT_WIDGET_MONEY_MAP_FILTER,
                            {
                                filter,
                            },
                        );
                    }}
                />
            </SproutWidgetFrame>
        );
    }

    if (widget === WidgetType.InfoCards) {
        return (
            <YStack width="100%" overflow="visible">
                <InfoCardsWidget
                    {...(widgetProps as unknown as InfoCardsWidgetProps)}
                    onEntranceComplete={onEntranceComplete}
                />
            </YStack>
        );
    }

    if (widget === WidgetType.DraftedMessageButton) {
        return null;
    }

    if (widget === WidgetType.SendToGuide) {
        return (
            <SproutWidgetFrame frosted>
                <SendToGuideWidget
                    {...(widgetProps as unknown as SendToGuideWidgetProps)}
                    messageId={messageId}
                />
            </SproutWidgetFrame>
        );
    }

    if (widget === WidgetType.EmailSupport) {
        return (
            <SproutWidgetFrame frosted>
                <EmailSupportWidget
                    {...(widgetProps as unknown as EmailSupportWidgetProps)}
                />
            </SproutWidgetFrame>
        );
    }

    if (widget === WidgetType.EstimatedBills) {
        return (
            <EstimatedBillsFlagGate
                widgetProps={
                    widgetProps as unknown as EstimatedBillsWidgetProps
                }
                isStreaming={isStreaming}
                onSendMessageWithAgentPayload={onSendMessageWithAgentPayload}
            />
        );
    }

    if (widget === WidgetType.IncomeInput) {
        return null;
    }

    if (widget === WidgetType.ExistingGoalsBadges) {
        return (
            <ExistingGoalsBadgesWidget
                {...(widgetProps as unknown as ExistingGoalsBadgesWidgetProps)}
            />
        );
    }

    return null;
};

/**
 * Wraps the EstimatedBills widget behind the feature flag. When the flag is
 * on, returns null (the interactive widget is rendered in the footer).
 * When off, falls back to the legacy InlineDataTable layout.
 */
const EstimatedBillsFlagGate = ({
    widgetProps,
}: {
    widgetProps: EstimatedBillsWidgetProps;
    isStreaming: boolean;
    onSendMessageWithAgentPayload: (
        displayText: string,
        agentPayload: string,
    ) => Promise<void>;
}) => {
    const { value: useBillsWidget } = useFeatureGate(
        FeatureFlags.ENABLE_CMM_EDITABLE_BILLS_WIDGET,
    );

    if (useBillsWidget) {
        return null;
    }

    const legacyTableProps: InlineDataTableWidgetProps = {
        accountColumnHeader: 'Bills (estimated)',
        totalsRowLabel: 'Total monthly bills',
        columns: [
            {
                key: 'amount',
                header: '',
                format: 'currency',
                align: 'right',
            },
        ],
        rows: widgetProps.bills.map((bill) => ({
            label: bill.label,
            cells: { amount: bill.amountCents },
        })),
    };

    return (
        <SproutWidgetFrame>
            <InlineDataTableWidget {...legacyTableProps} />
        </SproutWidgetFrame>
    );
};
